// Timeline scrubber over the dataset's time slices (52 weekly slices in the
// current build; the count is read from the manifest, never hard-coded).
//
// Pointer Events (one path for mouse + touch + pen). On pointerdown the track
// captures the pointer (setPointerCapture) and stops propagation so MapLibre's
// canvas never claims the gesture for pan/zoom; `touch-action: none` (set in
// CSS, scoped to the track only) stops the page scrolling while finger-dragging.
// Scrubbing fires onChange(index) synchronously -> a single gl.uniform1i +
// triggerRepaint, so the map updates in real time with no decode on the hot path.

import type { TimeSlice } from "../manifest";

export interface TimelineOptions {
  slices: TimeSlice[];
  initial: number;
  onChange: (index: number) => void;
}

export class Timeline {
  readonly el: HTMLElement;
  private track: HTMLElement;
  private fill: HTMLElement;
  private thumb: HTMLElement;
  private label: HTMLElement;
  private playBtn: HTMLButtonElement;
  private slices: TimeSlice[];
  private index: number;
  private onChange: (index: number) => void;
  private playTimer: number | null = null;

  constructor(opts: TimelineOptions) {
    this.slices = opts.slices;
    this.index = opts.initial;
    this.onChange = opts.onChange;

    this.el = document.createElement("div");
    this.el.className = "timeline";

    const top = document.createElement("div");
    top.className = "timeline-top";

    this.playBtn = document.createElement("button");
    this.playBtn.className = "play-btn";
    this.playBtn.type = "button";
    this.playBtn.setAttribute("aria-label", "Play / pause animation");
    this.playBtn.textContent = "▶"; // play triangle
    this.playBtn.addEventListener("click", () => this.togglePlay());

    this.label = document.createElement("div");
    this.label.className = "timeline-label";

    top.appendChild(this.playBtn);
    top.appendChild(this.label);

    this.track = document.createElement("div");
    this.track.className = "timeline-track";
    this.track.setAttribute("role", "slider");
    this.track.setAttribute("aria-label", "Time of year");
    this.track.setAttribute("aria-valuemin", "0");
    this.track.setAttribute("aria-valuemax", String(this.slices.length - 1));
    this.track.tabIndex = 0;

    this.fill = document.createElement("div");
    this.fill.className = "timeline-fill";
    this.thumb = document.createElement("div");
    this.thumb.className = "timeline-thumb";

    // Month tick marks: place one tick at the FIRST slice of each month, derived
    // from the slice ISO start dates (robust for weekly OR dekadal layouts —
    // weeks don't fall on neat 3-per-month boundaries). On very narrow screens a
    // CSS rule thins these to every other month.
    const ticks = document.createElement("div");
    ticks.className = "timeline-ticks";
    const seenMonth = new Set<number>();
    const last = Math.max(1, this.slices.length - 1);
    this.slices.forEach((s, i) => {
      const d = new Date(s.isoStart);
      const m = Number.isNaN(d.getTime()) ? -1 : d.getUTCMonth();
      if (m < 0 || seenMonth.has(m)) return;
      seenMonth.add(m);
      const t = document.createElement("div");
      t.className = "timeline-tick";
      t.style.left = `${(i / last) * 100}%`;
      const mn = document.createElement("span");
      mn.textContent = new Date(Date.UTC(2023, m, 1)).toLocaleString("en", {
        month: "short",
        timeZone: "UTC",
      });
      t.appendChild(mn);
      ticks.appendChild(t);
    });

    this.track.appendChild(this.fill);
    this.track.appendChild(this.thumb);
    this.el.appendChild(top);
    this.el.appendChild(this.track);
    this.el.appendChild(ticks);

    this.bindPointer();
    this.bindKeys();
    this.render(false);
  }

  private bindPointer() {
    const onMove = (e: PointerEvent) => {
      const rect = this.track.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const idx = Math.round(frac * (this.slices.length - 1));
      this.setIndex(idx);
    };
    this.track.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      e.stopPropagation(); // keep MapLibre from claiming the gesture
      this.stopPlay();
      this.track.setPointerCapture(e.pointerId);
      onMove(e);
    });
    this.track.addEventListener("pointermove", (e) => {
      if (!this.track.hasPointerCapture(e.pointerId)) return;
      e.preventDefault();
      e.stopPropagation();
      onMove(e);
    });
    const end = (e: PointerEvent) => {
      if (this.track.hasPointerCapture(e.pointerId)) {
        this.track.releasePointerCapture(e.pointerId);
      }
    };
    this.track.addEventListener("pointerup", end);
    this.track.addEventListener("pointercancel", end);
  }

  private bindKeys() {
    this.track.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        this.setIndex(this.index - 1);
        e.preventDefault();
      } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        this.setIndex(this.index + 1);
        e.preventDefault();
      } else if (e.key === "Home") {
        this.setIndex(0);
        e.preventDefault();
      } else if (e.key === "End") {
        this.setIndex(this.slices.length - 1);
        e.preventDefault();
      }
    });
  }

  setIndex(idx: number) {
    const clamped = Math.max(0, Math.min(this.slices.length - 1, idx));
    if (clamped === this.index) {
      this.render(false);
      return;
    }
    this.index = clamped;
    this.render(true);
  }

  get value(): number {
    return this.index;
  }

  private render(fireChange: boolean) {
    const frac = this.index / (this.slices.length - 1);
    this.fill.style.width = `${frac * 100}%`;
    this.thumb.style.left = `${frac * 100}%`;
    const s = this.slices[this.index];
    this.label.textContent = s.label;
    this.track.setAttribute("aria-valuenow", String(this.index));
    this.track.setAttribute("aria-valuetext", s.label);
    if (fireChange) this.onChange(this.index);
  }

  togglePlay() {
    if (this.playTimer !== null) this.stopPlay();
    else this.startPlay();
  }

  private startPlay() {
    this.playBtn.textContent = "❙❙"; // pause bars
    this.playTimer = window.setInterval(() => {
      this.setIndex((this.index + 1) % this.slices.length);
    }, 220);
  }

  private stopPlay() {
    if (this.playTimer !== null) {
      window.clearInterval(this.playTimer);
      this.playTimer = null;
    }
    this.playBtn.textContent = "▶";
  }
}
